/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState, useEffect, useCallback, Fragment, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {navigationIcArrowBack24px} from '@ey/motif-react-wrapper/assets/icons/index';
import {formNavigationButtonType} from '../../../../../../util/uiconstants';
import {getFrameEssentialQuery, labels} from '../../../../../../util/utils';
import Utility from '../../../../../../util/utilityfunctions';
import Button from '../../../../../Common/BaseComponentWrappers/MotifButton';
import Icon from '../../../../../Common/BaseComponentWrappers/MotifIcon';
import {useDocumentSwitcher, useQuery} from '../../../../../../util/customHooks';
import {useFrameData, useFrameKey} from '@ey/canvascoreservices';
import {isEmpty, orderBy} from 'lodash';

export default function FormNavigationButtonBodyDetailView({
	type,
	engagementId,
	documentTypeId,
	variant,
	styleName,
	isDrawerMode,
	knowledgeFormBodyId,
	formBodyId,
	setIsLoading
}) {
	const navigate = useNavigate();

	const query = useQuery();

	const queryParameters = useMemo(() => {
		return {
			isdrawer: isDrawerMode ?? false,
			target: query.target
		};
	}, [isDrawerMode, query.target]);

	const frameData = useFrameData();

	const {frameKey} = useFrameKey();

	const documentSwitcher = useDocumentSwitcher({includeReviewNotes: true, includeApprovals: true});

	const compare = useSelector((store) => store.aresCompareBodies);

	const engagement = useSelector((store) => store.engagement);

	const docs = useSelector((store) => store.documents);

	const [associatedDocumentIds, setAssociatedDocumentIds] = useState([]);

	const documents = useMemo(() => {
		return docs?.allIds?.map((id) => docs.byId[id]) ?? [];
	}, [docs]);

	const findAresTaskId = useCallback((document) => {
		let aresTaskId;

		if (document && !isEmpty(document)) {
			aresTaskId = document.tasks?.find((task) => task.isSystemAssociated)?.id;
			if (!aresTaskId) {
				aresTaskId = document.tasks?.[0]?.id;
			}
		}

		return aresTaskId;
	}, []);

	const compareBodies = useMemo(() => {
		if (Array.isArray(compare?.formBodies) && compare.formBodies.length > 0 && associatedDocumentIds.length > 0) {
			const bodies = compare.formBodies
				.filter((body) => {
					return body.knowledgeFormBodyId === knowledgeFormBodyId && associatedDocumentIds.includes(body.documentId);
				})
				.map((body) => {
					const document = documents?.find((doc) => doc.id === body.documentId);
					const stEntityId = document?.entities?.[0]?.id;

					const taskId = findAresTaskId(document);

					return {
						...body,
						taskId,
						documentName: document?.documentName,
						stEntityId
					};
				});

			return orderBy(bodies, [(body) => body?.documentName?.toLowerCase()], ['asc']);
		}

		return [];
	}, [compare, knowledgeFormBodyId, documents, findAresTaskId, associatedDocumentIds]);

	const [buttonData, setButtonData] = useState({});

	useEffect(() => {
		if (compareBodies.length > 0) {
			if (type === formNavigationButtonType.next) {
				setCompareNext();
			} else {
				setCompareBack();
			}
		}
	}, [compareBodies, formBodyId]);

	useEffect(() => {
		frameData.getData(frameKey).then((data) => {
			setAssociatedDocumentIds(data.associatedDocumentIds);
		});
	}, []);

	const getCompareStoryBookIndexById = useCallback(
		(formBodyId) => {
			return compareBodies.findIndex((body) => body.formBodyId === formBodyId);
		},
		[compareBodies]
	);

	const setCompareNext = useCallback(() => {
		let data = {};
		let isDisabled = false;

		const length = compareBodies.length;

		const currentIndex = getCompareStoryBookIndexById(formBodyId);

		if (currentIndex === length - 1) {
			isDisabled = true;
		} else if (currentIndex !== -1 && currentIndex + 1 <= length - 1) {
			data = compareBodies[currentIndex + 1];
		}

		setButtonData({
			type: formNavigationButtonType.next,
			isDisabled,
			canShow: true,
			...data
		});
	}, [compareBodies, formBodyId]);

	const setCompareBack = useCallback(() => {
		let data = {};
		let isDisabled = false;

		const currentIndex = getCompareStoryBookIndexById(formBodyId);

		if (currentIndex <= 0) {
			isDisabled = true;
		} else {
			data = compareBodies[currentIndex - 1];
		}

		setButtonData({
			type: formNavigationButtonType.back,
			isDisabled,
			text: '',
			canShow: true,
			...data
		});
	}, [compareBodies, formBodyId]);

	const onClick = useCallback(() => {
		const url = Utility.getAresTaskListNavigationUrl(
			buttonData.documentId,
			buttonData.formHeaderId,
			buttonData.formSectionId,
			engagementId,
			buttonData.taskId,
			getFrameEssentialQuery({
				bodyId: buttonData.formBodyId,
				isDrawer: true,
				isBodyDetailView: true
			}),
			documentTypeId,
			undefined,
			undefined,
			undefined,
			buttonData?.stEntityId
		);

		navigate(url);

		setIsLoading(true);

		documentSwitcher.switchDocument(buttonData.documentId).then(() => {
			setIsLoading(false);
		});
	}, [
		buttonData.documentId,
		buttonData.headerId,
		buttonData.sectionId,
		engagementId,
		queryParameters,
		documentTypeId,
		formBodyId,
		engagement,
		documentSwitcher
	]);

	if (buttonData.canShow) {
		return (
			<StyledFormNavigationButtonV2 className={`StyledFormNavigationButtonV2 ${styleName}`}>
				<Button type="button" variant={variant} onClick={onClick} disabled={buttonData.isDisabled}>
					<Fragment>
						{type === formNavigationButtonType.back && (
							<Icon
								className={`BackNavButton ${buttonData.isDisabled ? 'disabled' : ''}`}
								src={navigationIcArrowBack24px}
							/>
						)}
						{type === formNavigationButtonType.next && (
							<section className={`NextLabel NextNavButton ${buttonData.isDisabled ? 'disabled' : ''}`}>
								{labels.next}
							</section>
						)}
					</Fragment>
				</Button>
			</StyledFormNavigationButtonV2>
		);
	}

	return null;
}

const StyledFormNavigationButtonV2 = styled.section`
	.BackNavButton,
	.NextNavButton {
		&.disabled {
			opacity: 0.5;
			pointer-events: none;
		}
	}
`;
